import { isTouchDevice } from '@/app/utils/browser';
import { isIOSDevice } from '@/components/shared/utility/browser';
import logger from '@/lib/logger';

interface DownloadFile {
  name?: string;
  url: string;
  shareIOS?: boolean;
}

interface BlobFile {
  url: string;
}

const getBlobFile = async ({ url }: BlobFile): Promise<Blob | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error('File cannot be downloaded');
    }
    return blob;
  } catch (error) {
    logger.error('File error', { url, message: error.message });
    return null;
  }
};

/**
 * Downloads a file from a given URL
 *
 * @param {string} name - The name of the file to be downloaded (extension should be included)
 * @param {string} url - The URL of the file to be downloaded
 * @param {boolean} shareIOS - Using `navigator.share` to share the file on iOS (Support OS >= 17)
 * @return {Promise<void>} A promise that resolves when the file has been downloaded and shared
 */
export const downloadFile = async ({
  name,
  url,
  shareIOS = true,
}: DownloadFile): Promise<void> => {
  try {
    const fileName = name ?? 'download';
    const fileNameNoExt = name?.split('.')[0];

    const blob = await getBlobFile({ url });
    if (!blob) throw new Error('File cannot be downloaded');
    const ext = blob.type.split('/')[1]?.toLowerCase();

    const file = new File([blob], `${fileName}.${ext}`, {
      type: blob.type,
    });

    if (
      shareIOS &&
      isIOSDevice &&
      isTouchDevice &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      return await navigator.share({
        files: [file],
      });
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.rel = 'noopener';
    link.download = fileNameNoExt ?? 'download';

    document.body.appendChild(link);

    link.click();
    link.remove();

    URL.revokeObjectURL(link.href);
  } catch (error) {
    throw error;
  }
};
